import { useId } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { ChevronRight, FileText, FolderOpen, ListTodo, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { buildingBasePath } from "@/components/layout/buildingViewRoutes";
import { PERMISSIONS } from "@/constants/permissions";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { useProjectActivityDot } from "@/hooks/projects/useProjectActivity";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import type { OrgProjectOverview } from "@/stores/organizations/repository";
import type { ProjectActivityChannel } from "@/stores/organizations/projectActivityRepository";
import {
  portfolioPreferenceKey,
  usePortfolioPreference,
  writePortfolioPreference,
} from "./portfolioPreferences";

function projectDashboardPath(buildingId: string, projectId: number) {
  return `${buildingBasePath(buildingId)}/project-dashboard?project=${projectId}`;
}

function projectPath(buildingId: string, projectId: number, page: "documents" | "issues") {
  return `${buildingBasePath(buildingId)}/${page}?project=${projectId}`;
}

/** A subcontractor reaches a project's Documents only with a grant on it. */
function useCanOpenProjectDocuments(buildingId: string, projectId: number) {
  const perms = usePermissions();
  return (
    !perms.isSubcontractorOnly ||
    [PERMISSIONS.VIEW_DOCUMENTS_LIST_TIMELINE, PERMISSIONS.BUILDING_VIEW_PROJECTS].some(
      (permission) => perms.hasPermission(permission, { buildingId: Number(buildingId), projectId })
    )
  );
}

interface ShortcutProps {
  to: string;
  icon: typeof FileText;
  label: string;
  count: number;
  projectName: string;
  projectId: number;
  channel: ProjectActivityChannel;
  showActivity: boolean;
}

/** A count that opens the project's page, with a dot when it changed since the last visit. */
const ProjectShortcut = ({ icon: Icon, ...props }: ShortcutProps) => {
  const { t } = useLanguage();
  const unread = useProjectActivityDot(props.projectId, props.channel, props.showActivity);
  return (
    <Link
      to={props.to}
      aria-label={`${props.count} ${props.label} · ${props.projectName}`}
      title={`${props.count} ${props.label}`}
      className="inline-flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="relative inline-flex">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {unread && (
          <span
            className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-[#ef4129]"
            data-activity-dot={props.channel}
            aria-hidden="true"
          />
        )}
      </span>
      {unread && <span className="sr-only">{t.buildings.list.newActivity}</span>}
      <span className="tabular-nums">{props.count}</span>
    </Link>
  );
};

interface PortfolioProjectRowProps {
  buildingId: string;
  project: OrgProjectOverview;
  highlighted: boolean;
  showActivity: boolean;
}

const PortfolioProjectRow = observer(function PortfolioProjectRow(props: PortfolioProjectRowProps) {
  const { buildingId, project, highlighted, showActivity } = props;
  const { t } = useLanguage();
  const perms = usePermissions();
  const pmEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);
  const canOpenDocuments = useCanOpenProjectDocuments(buildingId, project.projectId);
  const canOpenIssues =
    pmEnabled &&
    perms.canViewPMTool &&
    (!perms.isSubcontractorOnly || perms.canViewPMOnProject(project.projectId));
  const name = (
    <>
      <FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{project.projectName}</span>
    </>
  );
  const nameClass = "inline-flex min-w-0 items-start gap-2 rounded text-sm font-medium";
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-3 py-2 hover:bg-[#f7f7f8]",
        highlighted && "bg-primary/5 ring-1 ring-inset ring-primary/20"
      )}
      data-project-row={project.projectId}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        {/* The name opens the project's dashboard, as a building dashboard's
            project tile does; the counts beside it go straight to their page. */}
        <Link
          to={projectDashboardPath(buildingId, project.projectId)}
          className={cn(
            nameClass,
            "hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
        >
          {name}
        </Link>
        <ProjectStatusBadge status={project.status} />
        {project.isDefault && (
          <span className="text-[11px] text-muted-foreground">
            {t.buildings.list.defaultProjectBadge}
          </span>
        )}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-3">
        {canOpenDocuments && (
          <ProjectShortcut
            to={projectPath(buildingId, project.projectId, "documents")}
            icon={FileText}
            label={t.buildings.list.docsLabel}
            count={project.docCount}
            projectName={project.projectName}
            projectId={project.projectId}
            channel="documents"
            showActivity={showActivity}
          />
        )}
        {canOpenIssues && (
          <ProjectShortcut
            to={projectPath(buildingId, project.projectId, "issues")}
            icon={ListTodo}
            label={t.buildings.list.issuesLabel}
            count={project.openIssueCount}
            projectName={project.projectName}
            projectId={project.projectId}
            channel="issues"
            showActivity={showActivity}
          />
        )}
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          aria-label={`${project.memberCount} ${t.buildings.list.membersLabel} · ${project.projectName}`}
          title={`${project.memberCount} ${t.buildings.list.membersLabel}`}
        >
          <Users className="h-4 w-4" aria-hidden="true" />
          <span className="tabular-nums">{project.memberCount}</span>
        </span>
      </div>
    </div>
  );
});

interface PortfolioProjectRowsProps {
  buildingId: string;
  projects: OrgProjectOverview[];
  isLoading: boolean;
  highlightedProjectIds: Set<number>;
  /** Open until the user collapses it — the layout decides, per portfolio size. */
  defaultExpanded: boolean;
  showActivity: boolean;
  /** Opens the create-project flow; absent when the viewer cannot create projects. */
  onAddProject?: () => void;
}

/** A building card's projects, behind a remembered per-building disclosure. */
export function PortfolioProjectRows(props: PortfolioProjectRowsProps) {
  const { buildingId, projects, isLoading, highlightedProjectIds, defaultExpanded } = props;
  const { t } = useLanguage();
  const { userId } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const key = portfolioPreferenceKey(userId, currentOrganization?.id, buildingId, "expanded");
  const preference = usePortfolioPreference(key);
  const hasMatches = projects.some((project) => highlightedProjectIds.has(project.projectId));
  // A search match is always shown; otherwise the user's choice, then the layout's.
  const expanded = hasMatches || (preference === null ? defaultExpanded : preference === "true");
  const rowsId = useId();
  if (isLoading) return <Skeleton className="ml-8 mt-2 h-8 max-w-lg" />;
  if (projects.length === 0) {
    return (
      <div className="ml-8 flex flex-wrap items-center gap-3 py-2 text-sm text-muted-foreground">
        <span>{t.buildings.list.noProjectsInBuilding}</span>
        {props.onAddProject && (
          <button
            type="button"
            onClick={props.onAddProject}
            className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t.buildings.list.addProjectLink}
          </button>
        )}
      </div>
    );
  }
  const rows = projects.map((project) => (
    <PortfolioProjectRow
      key={project.projectId}
      buildingId={buildingId}
      project={project}
      highlighted={highlightedProjectIds.has(project.projectId)}
      showActivity={props.showActivity}
    />
  ));
  // A single project is always shown — a disclosure around one row only hides it.
  if (projects.length === 1) {
    return (
      <div className="ml-8 min-w-0" data-project-list>
        {rows}
      </div>
    );
  }
  return (
    <div className="ml-8 min-w-0" data-project-list>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={rowsId}
        className={cn(
          "flex min-h-8 items-center gap-2 rounded text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          expanded && "mb-2"
        )}
        onClick={() => {
          if (key) writePortfolioPreference(key, String(!expanded));
        }}
      >
        <ChevronRight
          className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")}
          aria-hidden="true"
        />
        {t.buildings.list.projectsCount.replace("{count}", String(projects.length))}
      </button>
      <div id={rowsId} className="space-y-1">
        {expanded && rows}
      </div>
    </div>
  );
}
